package search.global

import analytics.AnalyticsCategory
import analytics.AnalyticsService
import apiclient.geoobjects.ObjectType
import apiclient.mapsearch.MapMarkerResult
import data.ObjectAndUserHandler
import data.objects.ActiveObjectStore
import data.objects.emptyGeoObjectDetails
import data.objects.views.tagManagement
import dev.fritz2.core.RenderContext
import dev.fritz2.core.RootStore
import dev.fritz2.core.autocomplete
import dev.fritz2.core.id
import dev.fritz2.core.placeholder
import dev.fritz2.core.tabIndex
import dev.fritz2.core.type
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import localization.TL
import localization.getString
import localization.getTranslationFlow
import localization.translate
import mainmenu.ActiveSearchMenuStore
import mainmenu.Pages
import mainmenu.RouterStore
import mainmenu.SearchMenu
import mainmenu.searchMenuContainerId
import mapsearch.globalsearch.GlobalSearchListResultsStore
import mapsearch.globalsearch.toMapMarkerResult
import objectrouting.NavigationStore
import objectrouting.NavigationToolToggleStore
import objectrouting.SearchHistoryStore
import org.w3c.dom.Element
import search.SearchDistanceCalculationStore
import search.distanceCalcSwitch
import search.geocoderResults
import search.globalSearchHeader
import search.mapMarkerSearchResultsList
import search.separationLine
import search.simpleListEntryMapMarkerResult
import search.simpleSelectedObjectMapMapMarkerResult
import search.twCoordinatesButtons
import services.SuggestedTagsContextStore
import theme.FormationIcons
import theme.FormationUIIcons
import twcomponents.doOnceWhenElementInDOM
import twcomponents.twCloseButton
import twcomponents.twColOfNoGap
import twcomponents.twIconLarge
import twcomponents.twIconMedium
import twcomponents.twInputField
import twcomponents.twInputTextField
import twcomponents.twLargeIconButtonNeutralRounded
import twcomponents.twLeftAlignedRow
import twcomponents.twMediumIconButton
import twcomponents.twMediumIconButtonNeutralRounded
import twcomponents.twMediumIconButtonNeutralRoundedGray
import twcomponents.twPageHeaderBack
import twcomponents.twRightAlignedButtonRow
import twcomponents.twRowOf
import twcomponents.twRowOfJustifyBetween
import twcomponents.twRowOfNoGap
import twcomponents.twSimpleTextTooltip
import twcomponents.twTitle
import twcomponents.twTooltip
import utils.ToggleStore
import utils.handleFunctions
import utils.toKeyWordTagsList
import web.dom.document
import webcomponents.KeywordTagActionType
import webcomponents.KeywordTagType
import webcomponents.baseLayout
import webcomponents.keywordTagList
import webcomponents.twContentScrollBox

fun RenderContext.pageSearch() {
    val globalSearchListResultsStore: GlobalSearchListResultsStore by koinCtx.inject()
    val searchInputFieldStore: SearchInputFieldStore by koinCtx.inject()
    val searchDistanceCalculationStore: SearchDistanceCalculationStore by koinCtx.inject()

    baseLayout(
        header = {
            twColOfNoGap("w-full gap-1") {
                globalSearchHeader(
                    title = TL.Search.PAGE_SEARCH_TITLE,
                    searchInputFieldStore = searchInputFieldStore,
                    withCoordinateDetection = true,
                )
                // filter buttons
//                searchFilterBar()
                // distance calculation switch
                distanceCalcSwitch(
                    distanceToSelectStore = searchDistanceCalculationStore,
                    switchHandler = searchDistanceCalculationStore.select,
                )
            }
        },
        content = {
            twContentScrollBox {
                // search results
                mapMarkerSearchResultsList(results = globalSearchListResultsStore.data.mapNotNull { it })
            }
        },
//        footerPaddings = null,
//        footer = {
//            inlinedBottomBar()
//        },
    )
}

enum class SearchComponentNav {
    Search, Filters
}

enum class SearchComponentSubNav {
    SearchResults,
//    Card,
}

class ActiveSearchComponentNavStore : RootStore<SearchComponentNav>(SearchComponentNav.Search, Job())
class ActiveSearchComponentSubNavStore : RootStore<SearchComponentSubNav>(SearchComponentSubNav.SearchResults, Job())
class SearchSelectedObjectStore : RootStore<MapMarkerResult?>(null, Job()) {
    val activeObjectStore: ActiveObjectStore by koinCtx.inject()

    fun pullFromActiveObjectStore() {
        val obj = activeObjectStore.current

        when (obj.objectType) {
            ObjectType.UserMarker, ObjectType.CurrentUserMarker -> {
                update(null)
            }

            else -> {
                if (obj != emptyGeoObjectDetails) {
                    update(obj.toMapMarkerResult())
                }
            }
        }
    }

    init {
        activeObjectStore.data.map { obj ->
            if (obj == emptyGeoObjectDetails) null else obj.toMapMarkerResult()
        } handledBy update
    }
}

fun setFocusOnSearchComponent() {
    document.getElementById(containerId)?.focus()
}

suspend fun setFocusOnSearchComponentWithDelay() {
    delay(300)
    document.getElementById(containerId)?.focus()
}

fun removeFocusOnSearchComponent() {
    document.getElementById(containerId)?.blur()
}

const val containerId = "searchMenu-globalSearch-container"
const val inputId = "searchMenu-globalSearch-input"
const val clearInputButtonId = "searchMenu-globalSearch-clearInputButton"
const val selectedObjectHolderId = "searchMenu-globalSearch-selectedObjectHolder"
const val navButtonId = "searchMenu-globalSearch-navButton"
const val filterButtonId = "searchMenu-globalSearch-filterButton"
const val menuButtonId = "searchMenu-globalSearch-menuButton"
const val clearHistoryButtonId = "searchMenu-globalSearch-clearHistoryButton"
const val toggleHistoryButtonId = "searchMenu-globalSearch-toggleHistoryButton"

fun RenderContext.searchComponent() {
    withKoin {
        val globalSearchListResultsStore: GlobalSearchListResultsStore = get()
        val searchInputFieldStore: SearchInputFieldStore = get()
        val activeSearchKeywordsStore: ActiveSearchKeywordsStore = get()
        val activeSearchObjectTypesStore: ActiveSearchObjectTypesStore = get()
        val activeSearchReadOnlyKeywordsStore: ActiveSearchReadOnlyKeywordsStore = get()
        val activeSearchFieldValuesStore: ActiveSearchFieldValuesStore = get()
        val objectAndUserHandler: ObjectAndUserHandler by koinCtx.inject()

        val tagsFlow = combine(
            activeSearchObjectTypesStore.data,
            activeSearchKeywordsStore.data,
            activeSearchReadOnlyKeywordsStore.data,
            activeSearchFieldValuesStore.data,
        ) { objTypes, keywords, otherTags, fieldValues ->
            objTypes.map { it.name }.toKeyWordTagsList(KeywordTagActionType.RemoveReadOnly) +
                otherTags.map { it.copy(actionType = KeywordTagActionType.RemoveReadOnly) } +
                keywords.toKeyWordTagsList(KeywordTagActionType.Remove) +
                fieldValues.toKeyWordTagsList(KeywordTagActionType.RemoveFieldValue)
        }

        val suggestedTagsContextStore: SuggestedTagsContextStore = get()
        val activeSearchComponentNavStore: ActiveSearchComponentNavStore = get()
        val activeSearchComponentSubNavStore: ActiveSearchComponentSubNavStore = get()
        val activeSearchMenuStore: ActiveSearchMenuStore = get()
        val navigationToolToggleStore: NavigationToolToggleStore = get()
        val searchSelectedObjectStore: SearchSelectedObjectStore = get()
        val navigationStore: NavigationStore = get()
        val routerStore: RouterStore = get()
        val analyticsService: AnalyticsService = get()
        val searchHistoryStore: SearchHistoryStore = get()

        val searchInputFocusStore = ToggleStore(false)

        searchInputFocusStore.data handledBy { active ->
            val menu = document.getElementById(searchMenuContainerId)
            if (active) {
                menu?.style?.zIndex = "1045"
            } else {
                menu?.style?.zIndex = "1009"
            }
        }

        fun removeFocusAndResetFocusStore() {
            searchInputFocusStore.update(false)
            removeFocusOnSearchComponent()
        }


        // Handle focus triggered by tags, input query state
        tagsFlow handledBy { tags ->
            if (tags.isNotEmpty() || searchInputFieldStore.current.isNotBlank()) {
                setFocusOnSearchComponentWithDelay()
            } else {
                removeFocusAndResetFocusStore()
            }
        }

        searchSelectedObjectStore.data.mapNotNull { it } handledBy { _ ->
            removeFocusAndResetFocusStore()
        }

        twColOfNoGap(
            additionalClasses = "w-full max-h-full pt-2 focus:outline-none rounded-2xl",
            id = containerId,
        ) {
            tabIndex(0)
            doOnceWhenElementInDOM(containerId) {
                document.getElementById(containerId)?.focus()
            }

            // Debounce to let event that triggers this focusout fire correctly before focusout fires
            focusouts.debounce(250) handledBy { e ->
                // event bubbled from input
                console.log("FocusOut Parent: ${(e.target as? Element)?.id}")
                console.log("FocusOut Related: ${(e.relatedTarget as? Element)?.id}")
                val relatedId = (e.relatedTarget as? Element)?.id
                val isRelated =
                    relatedId in listOf(
                        containerId,
                        inputId,
                        clearInputButtonId,
                        navButtonId,
                        filterButtonId,
                        menuButtonId,
                        clearHistoryButtonId,
                        toggleHistoryButtonId,
                        // "list-entry-button"
                    ) || relatedId?.startsWith("keywordTag") == true
                // Re-set focus if focus out event triggered by click on related child element
                if (isRelated) {
                    // except input
                    if (relatedId != inputId) {
                        setFocusOnSearchComponentWithDelay()
                    }
                    return@handledBy
                } else {
                    // event triggered from other
                    removeFocusAndResetFocusStore()
                    searchSelectedObjectStore.pullFromActiveObjectStore()
                }
            }
            focusins handledBy {
                searchInputFocusStore.update(true)
            }

            activeSearchComponentNavStore.data.render { searchNav ->
                when (searchNav) {
                    SearchComponentNav.Search -> {
                        // LINE CONTAINER FOR MAIN MENU BUTTON, SEARCH INPUT <-> SELECTED OBJECT, NAVIGATION BUTTON
                        twRowOfNoGap("items-center justify-between gap-1 px-2 pb-2 w-full") {
//                            className(searchSelectedObjectStore.data.map { if (it != null) "" else "pt-0.5" }) // pb-2 pb-3.5
                            // MAIN MENU BUTTON
                            twMediumIconButtonNeutralRounded(icon = FormationIcons.Menu) {
                                id(menuButtonId)
                                clicks handledBy {
//                                        activeSearchMenuStore.update(SearchMenu.Menu)
                                    routerStore.validateInternalRoute(Pages.Menu.route)
                                    analyticsService.analyticsEvent(AnalyticsCategory.MenuButton.click()())
                                }
                            }
                            // SCAN BUTTON
                            twLargeIconButtonNeutralRounded(icon = FormationUIIcons.ScanQR) {
                                clicks handledBy {
                                    routerStore.validateInternalRoute(Pages.Scan.route)
                                    analyticsService.analyticsEvent(AnalyticsCategory.ScanButton.click()())
                                }
                            }.twTooltip(positioning = PlacementValues.bottom, fallback = TL.BottomBar.SCAN.getString()) {
                                TL.BottomBar.SCAN.getTranslationFlow().renderText()
                            }

                            // SELECTED OBJECT
//                            combine(
//                                searchSelectedObjectStore.data,
//                                searchInputFieldStore.data.mapNotNull { if (it.isNotBlank()) null else true },
//                            ) { o, q -> Pair(o, q) }.render { (mapMarkerResult, _) ->
//                                if (searchInputFieldStore.current.isBlank() && mapMarkerResult != null) {
                            div("flex flex-row w-full items-center justify-center cursor-pointer rounded-xl bg-gray-100 hover:bg-gray-200 overflow-hidden") {
                                id(selectedObjectHolderId)
                                className(
                                    combine(
                                        searchSelectedObjectStore.data,
                                        searchInputFieldStore.data,
                                    ) { o, q ->
                                        if (q.isBlank() && o != null) {
                                            "block"
                                        } else "hidden"
                                    },
                                    initial = "hidden",
                                )
                                searchSelectedObjectStore.data.render { mapMarkerResult ->
                                    if (mapMarkerResult != null) {
                                        simpleSelectedObjectMapMapMarkerResult(mapMarkerResult = mapMarkerResult)
                                        clicks handledBy {
                                            activeSearchMenuStore.update(SearchMenu.Search)
                                            searchInputFieldStore.backToLastQuery(Unit)
                                            searchSelectedObjectStore.update(null)
                                            searchHistoryStore.add(mapMarkerResult)
                                            doOnceWhenElementInDOM(elementId = inputId) {
                                                document.getElementById(inputId)?.focus()
                                            }
                                        }
                                    }
                                }
                            }
//                                } else {
                            // SEARCH INPUT FIELD
                            twInputField(searchInputFieldStore) {
                                className(
                                    combine(
                                        searchSelectedObjectStore.data,
                                        searchInputFieldStore.data,
                                    ) { o, q ->
                                        if (q.isBlank() && o != null) {
                                            "hidden"
                                        } else "block"
                                    },
                                    initial = "block",
                                )
                                twLeftAlignedRow {
                                    twIconMedium(icon = FormationIcons.Search)
                                }
                                twInputTextField {
                                    id(inputId)
                                    type("search")
                                    autocomplete("off")
                                    placeholder(TL.Search.FIND_IT_FAST.getTranslationFlow())
                                }
                                twRightAlignedButtonRow {
                                    // Clear button with two functions
                                    searchInputFieldStore.data.render { text ->
                                        // 1. to clear search input
                                        if (text.isNotBlank()) {
                                            twCloseButton {
                                                id(clearInputButtonId)
                                                clicks handledBy {
                                                    searchInputFieldStore.clearLastAndWipe(Unit)
                                                    document.getElementById(inputId)?.focus()
                                                }
                                            }
                                        }
                                        // 2. clear focus of search widget, when input is already blank
                                        else {
                                            searchInputFocusStore.data.render { hasFocus ->
                                                if (hasFocus) {
                                                    twCloseButton {
                                                        clicks handledBy {
                                                            removeFocusAndResetFocusStore()
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
//                                }
//                            }

                            // TAGS FILTER BUTTON
                            twMediumIconButtonNeutralRoundedGray(icon = FormationIcons.Settings) {
                                id(filterButtonId)
                                clicks handledBy {
                                    activeSearchComponentNavStore.update(SearchComponentNav.Filters)
                                }
                            }.twTooltip(positioning = PlacementValues.bottomEnd) {
                                p("text-xs font-bold") {
                                    translate(TL.Search.MANAGE_SEARCH_FILTERS)
                                }
                            }

                            // NAVIGATION BUTTON
//                                twMediumIconButtonNeutralRounded(icon = FormationUIIcons.NavigationArrow) {
//                                    id(navButtonId)
//                                    clicks handledBy {
//                                        navigationToolToggleStore.update(true)
//                                        searchSelectedObjectStore.current?.let { obj ->
//                                            navigationStore.setDestinationObjectWithSourceAndLatLon(obj)
//                                        }
//                                        activeSearchMenuStore.update(SearchMenu.Navigation)
//                                    }
//                                }
                        }
                        activeSearchComponentSubNavStore.data.render { subNav ->
                            when (subNav) {
                                SearchComponentSubNav.SearchResults -> {
                                    // ACTIVE SEARCH TAGS
                                    combine(searchInputFocusStore.data, searchSelectedObjectStore.data, tagsFlow) { focus, obj, tags ->
                                        if (obj == null || focus) tags else listOf()
                                    }.render { combinedKeywords ->
                                        if (combinedKeywords.isNotEmpty()) {
                                            twColOfNoGap("w-full px-2 pb-2") {
                                                keywordTagList(
                                                    keywords = flowOf(combinedKeywords),
                                                    keywordTagType = KeywordTagType.SearchTag,
                                                )
                                            }
                                        }
                                    }
                                    // CONTAINER FOR SEARCH HISTORY, COORDINATE BUTTON & SEARCH RESULTS
                                    twColOfNoGap("max-h-full overflow-hidden") {
                                        className(
                                            searchInputFocusStore.data.map { hasFocus ->
                                                if (hasFocus) "visible" else "hidden"
                                            },
                                        )

                                        separationLine()

                                        // SCROLLABLE BOX
                                        div(
                                            "flex flex-col w-full max-h-full overflow-y-auto", // rounded-b-2xl md:max-w-100 bg-white border border-gray-300 shadow-lg
                                        ) {
                                            // SEARCH HISTORY (last 5 objects)
                                            combine(searchInputFocusStore.data, searchHistoryStore.data) { inputFocused, history ->
                                                Pair(inputFocused, history)
                                            }.render { (activeInput, objectHistory) ->
                                                if (activeInput && objectHistory.isNotEmpty()) {

                                                    twRowOfJustifyBetween(
                                                        "p-2 opacity-50 hover:bg-gray-200 cursor-pointer",
                                                        id = toggleHistoryButtonId,
                                                    ) {
                                                        twRowOf {
                                                            twIconMedium(icon = FormationIcons.History)
                                                            p("flex w-full items-center justify-center text-sm italic") {
                                                                translate(TL.Navigation.RECENTLY_VIEWED)
                                                            }
                                                            div {
                                                                className(searchHistoryStore.searchHistoryToggleStore.data.map { if (it) "rotate-180" else "" })
                                                                twIconMedium(icon = FormationUIIcons.ChevronDown)
                                                            }
                                                        }
                                                        twMediumIconButton(icon = FormationUIIcons.Close) {
                                                            id(clearHistoryButtonId)
                                                            clicks handledBy {
                                                                searchHistoryStore.clear(Unit)
                                                                setFocusOnSearchComponentWithDelay()
                                                            }
                                                        }.twSimpleTextTooltip(title = TL.Navigation.CLEAR_HISTORY, positioning = PlacementValues.left)

                                                        clicks handledBy searchHistoryStore.searchHistoryToggleStore.toggle
                                                    }

                                                    searchHistoryStore.searchHistoryToggleStore.data.render { showHistory ->
                                                        if (showHistory) {
                                                            objectHistory.forEach { mapMarkerResult ->
                                                                div("p-1 cursor-pointer opacity-50 hover:bg-gray-100") {
                                                                    simpleListEntryMapMarkerResult(mapMarkerResult)
                                                                    clicks handledBy {
                                                                        searchInputFieldStore.storeLastAndWipe(Unit)
                                                                        searchSelectedObjectStore.update(mapMarkerResult)
                                                                        searchHistoryStore.add(mapMarkerResult)
                                                                        objectAndUserHandler.updateAndLocateObjectOrUserByMapMarkerResult(mapMarkerResult)
                                                                        removeFocusAndResetFocusStore()
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                    separationLine()
                                                }
                                            }

                                            // OPTIONAL COORDINATE DETECTION
                                            twCoordinatesButtons(searchInputFieldStore)


                                            geocoderResults()

                                            // SEARCH RESULTS LIST
                                            mapMarkerSearchResultsList(
                                                results = globalSearchListResultsStore.data.mapNotNull { it }.debounce(100),
                                                simple = false,
                                            )
                                        }
                                    }

                                    // Close button for mobile to close search results (trigger focusOut)
//                                    if (isMobileOrTabletBrowser()) {
                                    twColOfNoGap("w-full") {
                                        className(
                                            searchInputFocusStore.data.map { hasFocus ->
                                                if (hasFocus) "visible" else "hidden"
                                            },
                                        )
                                        separationLine()
                                        div("flex flex-col items-center justify-center w-full py-2 cursor-pointer hover:text-gray-500") {
                                            twIconLarge(icon = FormationUIIcons.Close)
                                            clicks handledBy {
                                                removeFocusAndResetFocusStore()
                                            }
                                        }
                                    }
//                                    }
                                }

//                              SearchComponentSubNav.Pages -> {
//                                    pagesNavigator()
//                                }
                            }
                        }
                    }

                    SearchComponentNav.Filters -> {
                        div("max-h-full p-2 mb-4") { // mb-30 rounded-b-2xl
                            twPageHeaderBack(
                                routeBack = false,
                                additionalBackButtonHandler = handleFunctions {
                                    suggestedTagsContextStore.resetPrefix(Unit)
                                    activeSearchComponentNavStore.update(SearchComponentNav.Search)
                                    setFocusOnSearchComponent()
                                },
                            ) {
                                twTitle {
                                    translate(TL.Search.MANAGE_SEARCH_FILTERS)
                                }
                            }
                            twContentScrollBox("pt-4") {
                                tagManagement(KeywordTagType.SearchTag)
                            }
                        }
                    }
                }
            }
        }
    }
}
